import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ShieldCheck, Star, Plane, CheckCircle2, Loader2, ArrowRight, MapPin } from "lucide-react";

export default function CourierProfile() {
  const { uid } = useParams();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/delivery-network/couriers/${uid}/profile`).then(({ data }) => setP(data)).catch(() => {}).finally(() => setLoading(false));
  }, [uid]);

  if (loading) return <div className="grid place-items-center py-24"><Loader2 className="animate-spin text-emerald" size={28} /></div>;
  if (!p) return <div className="max-w-2xl mx-auto px-4 py-20 text-center text-muted-foreground">Traveller not found.</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="courier-profile">
      <div className="rounded-2xl sn-ink-bg sn-noise relative overflow-hidden p-7 text-white mb-6">
        <div className="flex items-center gap-4 relative">
          <span className="h-16 w-16 rounded-full bg-emerald grid place-items-center font-clash font-bold text-2xl">{(p.name || "T").charAt(0)}</span>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-clash font-bold text-2xl">{p.name}</h1>
              {p.verified && <span className="inline-flex items-center gap-1 text-[11px] font-bold text-ink bg-emerald px-2 py-0.5 rounded-full" data-testid="profile-verified"><ShieldCheck size={12} /> Verified</span>}
            </div>
            <p className="text-white/60 text-sm mt-1">Trusted traveller on the SHOP NOW Delivery Network</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="bg-card rounded-2xl border border-border p-4 text-center">
          <div className="font-display font-bold text-2xl flex items-center justify-center gap-1">{p.rating ? p.rating.avg : "—"}{p.rating && <Star size={16} className="fill-gold text-gold" />}</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">Rating {p.rating ? `(${p.rating.n})` : ""}</div>
        </div>
        <div className="bg-card rounded-2xl border border-border p-4 text-center">
          <div className="font-display font-bold text-2xl text-emerald">{p.completed}</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">Completed</div>
        </div>
        <div className="bg-card rounded-2xl border border-border p-4 text-center">
          <div className="font-display font-bold text-2xl">{p.active}</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">In progress</div>
        </div>
      </div>

      {p.trips.length > 0 && (
        <div className="bg-card rounded-2xl border border-border p-5 mb-6">
          <h3 className="font-display font-bold mb-3 flex items-center gap-2"><Plane size={16} className="text-emerald" /> Upcoming & listed trips</h3>
          <div className="space-y-2">
            {p.trips.map((t) => (
              <div key={t.id} className="text-sm flex items-center gap-2 text-muted-foreground"><MapPin size={13} className="text-emerald" /> {t.from_country} <ArrowRight size={12} /> {t.to_country}{t.travel_date ? ` · ${t.travel_date}` : ""}{t.capacity ? ` · ${t.capacity}` : ""}</div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-card rounded-2xl border border-border p-5">
        <h3 className="font-display font-bold mb-3 flex items-center gap-2"><Star size={16} className="text-gold" /> Rating history</h3>
        {p.ratings.length === 0 ? <p className="text-sm text-muted-foreground">No ratings yet.</p> : (
          <div className="space-y-3" data-testid="profile-ratings">
            {p.ratings.map((r) => (
              <div key={r.id} className="border-b border-border/60 pb-2">
                <div className="flex items-center gap-1 text-gold">{Array.from({ length: r.rating }).map((_, i) => <Star key={i} size={13} className="fill-gold text-gold" />)}</div>
                {r.comment && <p className="text-sm text-slate-700 mt-1">{r.comment}</p>}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-6 text-center">
        <Link to="/delivery-network" className="text-sm text-emerald font-semibold flex items-center justify-center gap-1"><CheckCircle2 size={15} /> Back to the Delivery Network</Link>
      </div>
    </div>
  );
}
